import {
  computeAgeFromDob,
  computeExperienceYearsFromProfessional
} from './candidateProfileUtils';

import { getJobStatusDistribution } from './jobStatus';

const INTERVIEW_STAGES = new Set(['interview', 'shortlisted', 'offer', 'joined', 'relieved']);
const OFFER_STAGES = new Set(['offer', 'joined', 'relieved']);
const JOINED_STAGES = new Set(['joined', 'relieved']);
const SHORTLIST_STAGES = new Set(['shortlisted', 'offer', 'joined', 'relieved']);

const REJECTION_LABELS = {
  communication: 'Communication',
  technical: 'Technical skills',
  attitude: 'Attitude / culture fit',
  experience: 'Experience mismatch',
  compensation: 'Compensation / expectations',
  availability: 'Availability / notice period',
  position_filled: 'Position filled',
  other: 'Other'
};

const OFFER_STATUS_LABELS = {
  pending: 'Pending',
  negotiating: 'Negotiating',
  accepted: 'Accepted',
  declined: 'Declined'
};

export function pct(numerator, denominator, digits = 1) {
  if (!denominator) return '0';
  return ((numerator / denominator) * 100).toFixed(digits);
}

export function formatRatio(numerator, denominator) {
  return `${numerator} / ${denominator}`;
}

export function isInterviewStage(stage) {
  return INTERVIEW_STAGES.has(stage);
}

export function isOfferStage(stage) {
  return OFFER_STAGES.has(stage);
}

export function isJoinedStage(stage) {
  return JOINED_STAGES.has(stage);
}

export function filterInterviewPool(applications = []) {
  return applications.filter(a => isInterviewStage(a.stage));
}

export function filterOfferPool(applications = []) {
  return applications.filter(a => isOfferStage(a.stage));
}

export function filterJoined(applications = []) {
  return applications.filter(a => isJoinedStage(a.stage));
}

export function filterShortlistPool(applications = []) {
  return applications.filter(a => SHORTLIST_STAGES.has(a.stage));
}

export function rejectionLabel(app) {
  const code = app?.rejectionReasonCode || 'other';
  return app?.rejectionReasonLabel || REJECTION_LABELS[code] || code;
}

export function offerStatusLabel(app) {
  const status = app?.offerStatus || 'pending';
  return app?.offerStatusLabel || OFFER_STATUS_LABELS[status] || status;
}

export function computeGlobalConversions(applications = []) {
  const interviewPool = filterInterviewPool(applications);
  const shortlistPool = filterShortlistPool(applications);
  const offerPool = filterOfferPool(applications);
  const joined = filterJoined(applications);
  const submitted = applications.filter(a => a.stage === 'submitted');
  const rejected = applications.filter(a => a.stage === 'rejected');
  const atOffer = applications.filter(a => a.stage === 'offer');

  return {
    totalApplications: applications.length,
    submittedCount: submitted.length,
    selectedCount: applications.filter(a => a.stage === 'selected').length,
    notSelectedCount: applications.filter(a => a.stage === 'not_selected').length,
    interviewCount: interviewPool.length,
    shortlistCount: shortlistPool.length,
    offerCount: offerPool.length,
    atOfferCount: atOffer.length,
    joinedCount: joined.length,
    rejectedCount: rejected.length,
    interviewToJoinedPct: pct(joined.length, interviewPool.length),
    offerToJoinedPct: pct(joined.length, offerPool.length),
    shortlistToOfferPct: pct(offerPool.length, shortlistPool.length),
    interviewToOfferPct: pct(offerPool.length, interviewPool.length),
    submitToInterviewPct: pct(interviewPool.length, applications.length),
    submitToJoinedPct: pct(joined.length, applications.length),
    interviewPool,
    shortlistPool,
    offerPool,
    atOffer,
    joined,
    submitted,
    rejected
  };
}

export function isJobCompleted(job, applications = []) {
  const positions = job.numberOfPositions || 1;
  const joined =
    job.joinedCount ??
    applications.filter(a => a.jobId === job.id && isJoinedStage(a.stage)).length;
  return job.status === 'closed' || joined >= positions;
}

export function computeJobAnalytics(jobs = [], applications = []) {
  return jobs.map(job => {
    const jobApps = applications.filter(a => a.jobId === job.id);
    const interviewPool = filterInterviewPool(jobApps);
    const offerPool = filterOfferPool(jobApps);
    const joined = filterJoined(jobApps);
    const positions = job.numberOfPositions || 1;
    const joinedCount = joined.length;
    const completed = isJobCompleted(job, applications);

    return {
      id: job.id,
      title: job.title || '—',
      clientName: job.clientName || job.clientId || '—',
      recruiterName: job.recruiterName || '—',
      status: job.status || '—',
      positions,
      submissions: jobApps.length,
      interviewCount: interviewPool.length,
      offerCount: offerPool.length,
      joinedCount,
      balanceToRecruit: Math.max(0, positions - joinedCount),
      interviewToJoinedPct: pct(joinedCount, interviewPool.length),
      offerToJoinedPct: pct(joinedCount, offerPool.length),
      fillPct: pct(joinedCount, positions),
      completed,
      interviewPool,
      offerPool,
      joined,
      allApps: jobApps
    };
  });
}

export function computeClientAnalytics(clients = [], jobs = [], applications = []) {
  return clients.map(client => {
    const clientJobs = jobs.filter(j => j.clientId === client.id);
    const jobIds = new Set(clientJobs.map(j => j.id));
    const clientApps = applications.filter(a => jobIds.has(a.jobId));
    const interviewPool = filterInterviewPool(clientApps);
    const offerPool = filterOfferPool(clientApps);
    const joined = filterJoined(clientApps);
    const completedJobs = clientJobs.filter(j => isJobCompleted(j, applications)).length;

    return {
      id: client.id,
      name: client.name || '—',
      jobCount: clientJobs.length,
      activeJobs: clientJobs.filter(j => ['open', 'in-progress', 'on-hold'].includes(j.status)).length,
      completedJobs,
      submissions: clientApps.length,
      interviewCount: interviewPool.length,
      offerCount: offerPool.length,
      joinedCount: joined.length,
      interviewToJoinedPct: pct(joined.length, interviewPool.length),
      offerToJoinedPct: pct(joined.length, offerPool.length),
      jobsCompletedPct: pct(completedJobs, clientJobs.length),
      interviewPool,
      offerPool,
      joined,
      clientJobs,
      clientApps
    };
  });
}

export function computeRecruiterAnalytics(recruiters = [], jobs = [], applications = []) {
  return recruiters.map(recruiter => {
    const recruiterJobs = jobs.filter(j => j.recruiterId === recruiter.id);
    const jobIds = new Set(recruiterJobs.map(j => j.id));
    const recruiterApps = applications.filter(a => jobIds.has(a.jobId));
    const interviewPool = filterInterviewPool(recruiterApps);
    const offerPool = filterOfferPool(recruiterApps);
    const joined = filterJoined(recruiterApps);
    const completedJobs = recruiterJobs.filter(j => isJobCompleted(j, applications));
    const activeJobs = recruiterJobs.filter(j => ['open', 'in-progress', 'on-hold'].includes(j.status));

    return {
      id: recruiter.id,
      name: recruiter.name || '—',
      email: recruiter.email || '—',
      assignedJobs: recruiterJobs.length,
      activeJobs: activeJobs.length,
      completedJobs: completedJobs.length,
      jobsCompletedPct: pct(completedJobs.length, recruiterJobs.length),
      submissions: recruiterApps.length,
      interviewCount: interviewPool.length,
      offerCount: offerPool.length,
      joinedCount: joined.length,
      interviewToJoinedPct: pct(joined.length, interviewPool.length),
      offerToJoinedPct: pct(joined.length, offerPool.length),
      successRate: pct(joined.length, recruiterApps.length),
      interviewPool,
      offerPool,
      joined,
      recruiterJobs,
      recruiterApps,
      completedJobList: completedJobs,
      activeJobList: activeJobs
    };
  });
}

export function ageBandFromDob(dob) {
  const age = computeAgeFromDob(dob);
  if (age == null) return 'Unknown';
  if (age < 18) return 'Under 18';
  if (age <= 25) return '18–25';
  if (age <= 35) return '26–35';
  if (age <= 45) return '36–45';
  return '46+';
}

export function experienceBandFromCandidate(candidate) {
  const fromRows = computeExperienceYearsFromProfessional(candidate?.professional);
  const fallback = candidate?.experienceFrom ?? candidate?.experienceTo;
  const years =
    fromRows != null
      ? fromRows
      : fallback != null && fallback !== '' && Number.isFinite(Number(fallback))
        ? Number(fallback)
        : null;
  return experienceBandFromYears(years);
}

export function experienceBandFromYears(years) {
  if (years == null) return 'Unknown';
  if (years < 2) return '0–2 yrs';
  if (years < 5) return '2–5 yrs';
  if (years < 10) return '5–10 yrs';
  return '10+ yrs';
}

export function primaryEducationLabel(candidate) {
  const qual = String(candidate?.educationalQualification || '').trim();
  if (qual) return qual;
  const rows = Array.isArray(candidate?.educational) ? candidate.educational : [];
  const degree = rows.map(r => String(r?.degree || '').trim()).filter(Boolean);
  return degree[0] || 'Not specified';
}

export function candidateDisplayName(candidate) {
  return (
    candidate?.displayName ||
    candidate?.personal?.name ||
    candidate?.contact?.name ||
    candidate?.id ||
    '—'
  );
}

export function candidatesInPipeline(applications = [], candidates = []) {
  const ids = new Set(applications.map(a => a.candidateId).filter(Boolean));
  return (candidates || []).filter(c => ids.has(c.id));
}

export function mapCandidateDemographics(candidate) {
  const expYears = computeExperienceYearsFromProfessional(candidate?.professional);
  const fallback = candidate?.experienceFrom ?? candidate?.experienceTo;
  const experienceYears =
    expYears != null
      ? expYears
      : fallback != null && fallback !== '' && Number.isFinite(Number(fallback))
        ? Number(fallback)
        : null;

  return {
    id: candidate.id,
    name: candidateDisplayName(candidate),
    email: candidate.displayEmail || candidate.contact?.email || '—',
    location: candidate.location || candidate.personal?.city || '—',
    age: computeAgeFromDob(candidate?.personal?.dateOfBirth ?? candidate?.personal?.dob),
    ageBand: ageBandFromDob(candidate?.personal?.dateOfBirth ?? candidate?.personal?.dob),
    experienceYears: experienceYears ?? '—',
    experienceBand: experienceBandFromCandidate(candidate),
    education: primaryEducationLabel(candidate),
    expertise: (candidate.areasOfExpertise || []).join(', ') || 'Not specified',
    skills: (candidate.skills || []).join(', ') || '—',
    technicalSkills: (candidate.technicalSkills || []).join(', ') || '—',
    gender: candidate.personal?.gender || '—',
    _candidate: candidate
  };
}

export function countByField(rows, field, emptyLabel = 'Unknown') {
  const counts = {};
  for (const row of rows) {
    const raw = row[field];
    const key = raw == null || raw === '' || raw === '—' ? emptyLabel : String(raw);
    counts[key] = (counts[key] || 0) + 1;
  }
  return counts;
}

export function countExpertiseAreas(candidates = []) {
  return countMultiValueField(candidates, 'areasOfExpertise', 'Not specified');
}

export function countTechnicalSkills(candidates = []) {
  return countMultiValueField(candidates, 'technicalSkills', 'Not specified');
}

export function countCoreCompetencies(candidates = []) {
  return countMultiValueField(candidates, 'skills', 'Not specified');
}

export function countMultiValueField(candidates = [], field, emptyLabel = 'Not specified') {
  const counts = {};
  for (const c of candidates) {
    const values = c[field]?.length ? c[field] : [emptyLabel];
    for (const value of values) {
      const key = String(value || emptyLabel).trim() || emptyLabel;
      counts[key] = (counts[key] || 0) + 1;
    }
  }
  return counts;
}

export function countLocations(candidates = []) {
  const counts = {};
  for (const c of candidates) {
    const loc = String(c.location || c.personal?.city || '').trim() || 'Not specified';
    counts[loc] = (counts[loc] || 0) + 1;
  }
  return counts;
}

export function computeStageDistribution(applications = []) {
  const stages = [
    'submitted',
    'selected',
    'not_selected',
    'interview',
    'shortlisted',
    'offer',
    'joined',
    'relieved',
    'rejected'
  ];
  const data = {};
  for (const stage of stages) {
    const count = applications.filter(a => a.stage === stage).length;
    if (count > 0) data[stage] = count;
  }
  return data;
}

export function computeCandidateAnalytics(applications = [], candidates = []) {
  const grouped = {};

  for (const candidate of candidates || []) {
    grouped[candidate.id] = {
      id: candidate.id,
      name: candidateDisplayName(candidate),
      email: candidate.displayEmail || candidate.contact?.email || '—',
      applications: [],
      stages: new Set()
    };
  }

  for (const app of applications || []) {
    const cid = app.candidateId;
    if (!cid) continue;
    if (!grouped[cid]) {
      grouped[cid] = {
        id: cid,
        name: app.candidateName || cid,
        email: '—',
        applications: [],
        stages: new Set()
      };
    }
    grouped[cid].applications.push(app);
    grouped[cid].stages.add(app.stage);
  }

  return Object.values(grouped)
    .map(row => {
      const apps = row.applications;
      const interviewPool = filterInterviewPool(apps);
      const offerPool = filterOfferPool(apps);
      const joined = filterJoined(apps);
      const activeStages = [...row.stages].filter(s => !['joined', 'relieved', 'rejected'].includes(s));
      return {
        id: row.id,
        name: row.name,
        email: row.email,
        applicationCount: apps.length,
        activeStages: activeStages.join(', ') || '—',
        bestStage: bestPipelineStage(apps),
        interviewCount: interviewPool.length,
        offerCount: offerPool.length,
        joinedCount: joined.length,
        interviewToJoinedPct: pct(joined.length, interviewPool.length),
        allApps: apps,
        joined
      };
    })
    .sort((a, b) => b.applicationCount - a.applicationCount || a.name.localeCompare(b.name));
}

function bestPipelineStage(apps = []) {
  const order = ['joined', 'relieved', 'offer', 'shortlisted', 'interview', 'submitted', 'rejected'];
  for (const stage of order) {
    if (apps.some(a => a.stage === stage)) return stage;
  }
  return '—';
}

export function computeFresherSplit(candidates = []) {
  const counts = { Fresher: 0, Experienced: 0, Unknown: 0 };
  for (const c of candidates || []) {
    if (c.isFresher === true) counts.Fresher += 1;
    else if (c.isFresher === false) counts.Experienced += 1;
    else {
      const exp = computeExperienceYearsFromProfessional(c?.professional);
      if (exp != null && exp < 1) counts.Fresher += 1;
      else if (exp != null) counts.Experienced += 1;
      else counts.Unknown += 1;
    }
  }
  return counts;
}

export function computeRejectionAnalytics(applications = []) {
  const rejected = applications.filter(a => a.stage === 'rejected');
  const counts = {};
  for (const app of rejected) {
    const label = rejectionLabel(app);
    counts[label] = (counts[label] || 0) + 1;
  }
  return { rejected, counts: sortCountDesc(counts), total: rejected.length };
}

export function computeOfferStatusAnalytics(applications = []) {
  const atOffer = applications.filter(a => a.stage === 'offer');
  const counts = {};
  for (const app of atOffer) {
    const label = offerStatusLabel(app);
    counts[label] = (counts[label] || 0) + 1;
  }
  return { atOffer, counts: sortCountDesc(counts), total: atOffer.length };
}

export function computeJobStatusAnalytics(jobs = []) {
  const raw = getJobStatusDistribution(jobs);
  const labels = {
    open: 'Open',
    'in-progress': 'In progress',
    'on-hold': 'On hold',
    closed: 'Closed',
    inactive: 'Inactive'
  };
  const counts = {};
  for (const [key, value] of Object.entries(raw)) {
    if (value > 0) counts[labels[key] || key] = value;
  }
  return { counts: sortCountDesc(counts), jobs, total: jobs.length };
}

export function computeBillingAnalytics(invoices = []) {
  const byStatus = {};
  for (const inv of invoices || []) {
    const status = String(inv.status || 'draft');
    const label = status.charAt(0).toUpperCase() + status.slice(1);
    byStatus[label] = (byStatus[label] || 0) + 1;
  }
  return {
    total: invoices?.length || 0,
    paid: invoices?.filter(i => i.status === 'paid').length || 0,
    sent: invoices?.filter(i => i.status === 'sent').length || 0,
    draft: invoices?.filter(i => i.status === 'draft').length || 0,
    byStatus: sortCountDesc(byStatus),
    invoices: invoices || []
  };
}

export function filterRejectionBucket(applications, bucketKey) {
  return applications.filter(a => a.stage === 'rejected' && rejectionLabel(a) === bucketKey);
}

export function filterOfferStatusBucket(applications, bucketKey) {
  return applications.filter(a => a.stage === 'offer' && offerStatusLabel(a) === bucketKey);
}

export function filterJobStatusBucket(jobs, bucketKey) {
  const reverse = {
    Open: 'open',
    'In progress': 'in-progress',
    'On hold': 'on-hold',
    Closed: 'closed',
    Inactive: 'inactive'
  };
  const status = reverse[bucketKey] || bucketKey;
  return jobs.filter(j => j.status === status);
}

export function fresherBucketForCandidate(candidate) {
  if (!candidate) return 'Unknown';
  if (candidate.isFresher === true) return 'Fresher';
  if (candidate.isFresher === false) return 'Experienced';
  const exp = computeExperienceYearsFromProfessional(candidate?.professional);
  if (exp != null && exp < 1) return 'Fresher';
  if (exp != null) return 'Experienced';
  return 'Unknown';
}

export function filterFresherBucket(rows, bucketKey, pipelineCandidates = []) {
  const byId = Object.fromEntries(pipelineCandidates.map(c => [c.id, c]));
  return rows.filter(r => {
    const c = r._candidate || byId[r.id];
    return fresherBucketForCandidate(c) === bucketKey;
  });
}

export function filterCoreCompetencyRows(rows, bucketKey, pipelineCandidates = []) {
  return rows.filter(r => {
    const skills = r._candidate?.skills?.length ? r._candidate.skills : ['Not specified'];
    return skills.some(s => (String(s).trim() || 'Not specified') === bucketKey);
  });
}

export function computeDemographics(applications = [], candidates = []) {
  const pool = candidates || [];
  const rows = pool.map(mapCandidateDemographics);
  const withApplications = candidatesInPipeline(applications, pool);

  return {
    /** All candidates in scope (matches Candidates list / DB count). */
    candidateCount: pool.length,
    /** @deprecated use candidateCount — kept for older callers */
    pipelineCount: pool.length,
    rows,
    /** Full candidate records for drill-down filters */
    allCandidates: pool,
    /** @deprecated use allCandidates */
    pipelineCandidates: pool,
    withApplicationsCount: withApplications.length,
    ageBands: countByField(rows, 'ageBand'),
    experienceBands: countByField(rows, 'experienceBand'),
    education: countByField(rows, 'education', 'Not specified'),
    expertise: countExpertiseAreas(pool),
    coreCompetencies: countCoreCompetencies(pool),
    technicalSkills: countTechnicalSkills(pool),
    fresherSplit: computeFresherSplit(pool),
    locations: countLocations(pool),
    gender: countByField(rows, 'gender', 'Not specified')
  };
}

export function sortCountDesc(data) {
  return Object.fromEntries(
    Object.entries(data).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  );
}

export function topEntries(data, limit = 12) {
  const sorted = Object.entries(sortCountDesc(data));
  return Object.fromEntries(sorted.slice(0, limit));
}

export function truncateChartLabel(label, max = 22) {
  const text = String(label || '—').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

const ENTITY_METRIC_EXTRACTORS = {
  submissions: r => Number(r.submissions ?? r.applicationCount ?? 0),
  joined: r => Number(r.joinedCount ?? 0),
  interview: r => Number(r.interviewCount ?? 0),
  offer: r => Number(r.offerCount ?? 0),
  interviewToJoined: r => Number(r.interviewToJoinedPct) || 0,
  offerToJoined: r => Number(r.offerToJoinedPct) || 0,
  fillRate: r => Number(r.fillPct) || 0,
  jobsCompleted: r => Number(r.jobsCompletedPct) || 0,
  successRate: r => Number(r.successRate) || 0,
  assignedJobs: r => Number(r.assignedJobs ?? r.jobCount ?? 0),
  completedJobs: r => Number(r.completedJobs ?? 0),
  activeJobs: r => Number(r.activeJobs ?? 0)
};

const PERCENTAGE_METRICS = new Set([
  'interviewToJoined',
  'offerToJoined',
  'fillRate',
  'jobsCompleted',
  'successRate'
]);

export const ENTITY_ANALYTICS_CONFIG = {
  job: {
    label: 'Jobs',
    labelKey: 'title',
    metrics: [
      { id: 'joined', label: 'Joined hires' },
      { id: 'submissions', label: 'Submissions' },
      { id: 'interview', label: 'Interview+' },
      { id: 'offer', label: 'Offer+' },
      { id: 'pipelineCompare', label: 'Interview+ vs Joined' },
      { id: 'interviewToJoined', label: 'Interview→Joined %' },
      { id: 'offerToJoined', label: 'Offer→Joined %' },
      { id: 'fillRate', label: 'Fill rate %' }
    ]
  },
  client: {
    label: 'Clients',
    labelKey: 'name',
    metrics: [
      { id: 'joined', label: 'Joined hires' },
      { id: 'submissions', label: 'Submissions' },
      { id: 'pipelineCompare', label: 'Interview+ vs Joined' },
      { id: 'jobsCompleted', label: 'Jobs completed %' },
      { id: 'completedJobs', label: 'Completed jobs' },
      { id: 'interviewToJoined', label: 'Interview→Joined %' },
      { id: 'offerToJoined', label: 'Offer→Joined %' }
    ]
  },
  recruiter: {
    label: 'Recruiters',
    labelKey: 'name',
    metrics: [
      { id: 'joined', label: 'Joined hires' },
      { id: 'submissions', label: 'Submissions' },
      { id: 'assignedJobs', label: 'Jobs assigned' },
      { id: 'completedJobs', label: 'Jobs completed' },
      { id: 'jobsCompleted', label: 'Jobs completed %' },
      { id: 'pipelineCompare', label: 'Interview+ vs Joined' },
      { id: 'interviewToJoined', label: 'Interview→Joined %' },
      { id: 'offerToJoined', label: 'Offer→Joined %' },
      { id: 'successRate', label: 'Success rate %' }
    ]
  },
  candidate: {
    label: 'Candidates',
    labelKey: 'name',
    metrics: [
      { id: 'submissions', label: 'Applications' },
      { id: 'joined', label: 'Joined' },
      { id: 'pipelineCompare', label: 'Interview+ vs Joined' },
      { id: 'interviewToJoined', label: 'Interview→Joined %' }
    ]
  }
};

export function entityTypesForRole(role) {
  if (role === 'admin') return ['job', 'client', 'recruiter', 'candidate'];
  if (role === 'recruiter') return ['job', 'recruiter', 'candidate'];
  if (role === 'client') return ['job', 'candidate'];
  return ['job'];
}

export function isPercentageEntityMetric(metricId) {
  return PERCENTAGE_METRICS.has(metricId);
}

export function buildEntityChartData(rows = [], metricKey, labelKey = 'title', limit = 12) {
  if (metricKey === 'pipelineCompare') {
    return buildEntityPipelineCompare(rows, labelKey, limit);
  }

  const extract = ENTITY_METRIC_EXTRACTORS[metricKey] || ENTITY_METRIC_EXTRACTORS.joined;
  const sorted = [...rows].sort((a, b) => extract(b) - extract(a) || String(a[labelKey] || '').localeCompare(String(b[labelKey] || '')));
  const top = sorted.filter(r => extract(r) > 0).slice(0, limit);
  const used = sorted.slice(0, limit);

  const chartData = {};
  const rowByLabel = {};
  for (const row of used.length ? used : top) {
    const raw = row[labelKey] || row.name || row.id;
    let label = truncateChartLabel(raw);
    if (rowByLabel[label]) label = `${label} (${row.id})`;
    chartData[label] = extract(row);
    rowByLabel[label] = row;
  }

  return { chartData, rowByLabel, isPercentage: isPercentageEntityMetric(metricKey) };
}

export function buildEntityPipelineCompare(rows = [], labelKey = 'title', limit = 10) {
  const sorted = [...rows].sort(
    (a, b) =>
      Number(b.interviewCount ?? 0) - Number(a.interviewCount ?? 0) ||
      String(a[labelKey] || '').localeCompare(String(b[labelKey] || ''))
  );
  const top = sorted.filter(r => Number(r.interviewCount ?? 0) > 0 || Number(r.joinedCount ?? 0) > 0).slice(0, limit);

  const groups = top.map(row => {
    const raw = row[labelKey] || row.name || row.id;
    return {
      id: row.id,
      label: truncateChartLabel(raw),
      fullLabel: raw,
      values: {
        'Interview+': Number(row.interviewCount ?? 0),
        Joined: Number(row.joinedCount ?? 0)
      },
      row
    };
  });

  return { groups, isComparison: true };
}

export function filterDemographicRows(rows, chartKey, bucketKey, pipelineCandidates = []) {
  if (chartKey === 'expertise') {
    return rows.filter(r => {
      const areas = r._candidate?.areasOfExpertise?.length
        ? r._candidate.areasOfExpertise
        : ['Not specified'];
      return areas.some(a => (String(a).trim() || 'Not specified') === bucketKey);
    });
  }
  if (chartKey === 'technicalSkills') {
    return rows.filter(r => {
      const skills = r._candidate?.technicalSkills?.length
        ? r._candidate.technicalSkills
        : ['Not specified'];
      return skills.some(s => (String(s).trim() || 'Not specified') === bucketKey);
    });
  }
  if (chartKey === 'coreCompetencies') {
    return filterCoreCompetencyRows(rows, bucketKey, pipelineCandidates);
  }
  if (chartKey === 'fresher') {
    return filterFresherBucket(rows, bucketKey, pipelineCandidates);
  }
  if (chartKey === 'location') {
    return rows.filter(r => {
      const loc = String(r._candidate?.location || r._candidate?.personal?.city || '').trim() || 'Not specified';
      return loc === bucketKey;
    });
  }
  const fieldMap = {
    age: 'ageBand',
    experience: 'experienceBand',
    education: 'education',
    gender: 'gender'
  };
  const field = fieldMap[chartKey];
  if (!field) return rows;
  return rows.filter(r => r[field] === bucketKey);
}
